import { motion } from 'framer-motion';
import { useState } from 'react';
import { useThemePreference } from '../../theme/ThemePreferenceProvider';
import type { OnboardingLanguage } from '../constants';

type PathOption = 'traditional' | 'quick_start';

type PathVariant = 'traditional' | 'quick_start';

interface PathSelectStepProps {
  language?: OnboardingLanguage;
  onSelectTraditional: () => void;
  onSelectQuickStart: () => void;
  onBack: () => void;
}

const ADVANCE_DELAY_MS = 140;

const getPathCardStyle = (isActive: boolean, variant: PathVariant, theme: 'light' | 'dark') => {
  const palettes: Record<PathVariant, { glow: string; softTint: string; accent: string; border: string; badgeBg: string; badgeText: string; badgeBorder: string }> = {
    traditional: {
      glow: 'rgba(196, 132, 252, 0.34)',
      softTint: 'rgba(251, 191, 211, 0.24)',
      accent: 'rgba(216, 180, 254, 0.65)',
      border: 'rgba(236, 197, 255, 0.8)',
      badgeBg: 'rgba(245, 220, 255, 0.92)',
      badgeText: '#3a195e',
      badgeBorder: 'rgba(243, 213, 255, 0.92)',
    },
    quick_start: {
      glow: 'rgba(103, 232, 249, 0.32)',
      softTint: 'rgba(129, 140, 248, 0.24)',
      accent: 'rgba(147, 197, 253, 0.64)',
      border: 'rgba(186, 230, 253, 0.82)',
      badgeBg: 'rgba(207, 250, 254, 0.92)',
      badgeText: '#10364a',
      badgeBorder: 'rgba(186, 230, 253, 0.88)',
    },
  };

  const palette = palettes[variant];

  const isLight = theme === 'light';

  return {
    palette,
    cardStyle: isActive
      ? {
          boxShadow: isLight
            ? `0 0 0 1px ${palette.border}, 0 16px 32px rgba(15,23,42,0.13), 0 10px 22px ${palette.softTint}`
            : `0 0 0 1px rgba(255,255,255,0.26), 0 0 30px ${palette.glow}, 0 0 56px ${palette.softTint}`,
          borderColor: isLight ? palette.badgeBorder : palette.border,
          background: isLight
            ? variant === 'traditional'
              ? 'linear-gradient(180deg, rgba(254,245,255,0.98), rgba(252,244,255,0.94))'
              : 'linear-gradient(180deg, rgba(241,252,255,0.98), rgba(238,248,255,0.95))'
            : `color-mix(in srgb, ${palette.softTint} 52%, rgba(8,14,38,0.78))`,
        }
      : {
          background: isLight
            ? 'linear-gradient(180deg, rgba(255,255,255,0.98), rgba(248,250,252,0.94))'
            : 'linear-gradient(165deg, rgba(14,20,44,0.84) 0%, rgba(11,17,38,0.7) 55%, rgba(8,14,33,0.84) 100%)',
          borderColor: isLight ? 'rgba(148,163,184,0.24)' : undefined,
          boxShadow: isLight ? '0 14px 34px rgba(15,23,42,0.08)' : undefined,
        },
    overlayStyle: {
      background: isLight
        ? `radial-gradient(circle at 18% 8%, color-mix(in srgb, ${palette.accent} 16%, transparent) 0%, color-mix(in srgb, ${palette.accent} 10%, transparent) 32%, color-mix(in srgb, ${palette.accent} 5%, transparent) 62%, transparent 100%)`
        : `radial-gradient(circle at 18% 8%, color-mix(in srgb, ${palette.accent} 34%, transparent) 0%, color-mix(in srgb, ${palette.accent} 20%, transparent) 30%, color-mix(in srgb, ${palette.accent} 12%, transparent) 58%, transparent 100%)`,
    },
  };
};

export function PathSelectStep({ language = 'es', onSelectTraditional, onSelectQuickStart, onBack }: PathSelectStepProps) {
  const { theme } = useThemePreference();
  const [selectedPath, setSelectedPath] = useState<PathOption | null>(null);
  const [isAdvancing, setIsAdvancing] = useState(false);

  const copy = language === 'en'
    ? {
        step: 'Step 2 · Choose your path',
        title: 'How do you want to start today?',
        subtitle: 'Choose the way that best matches your momentum right now.',
        personalTitle: 'Personal guide',
        personalDescription: 'Answer the full onboarding and get a tailored plan based on your rhythm.',
        personalDuration: '3-4 min',
        quickTitle: 'Quick Start',
        quickDescription: 'Go live in under a minute with curated tasks and a compact setup.',
        quickDuration: '< 1 min',
        quickLabel: 'Fast lane',
        back: 'Back',
        selected: 'Selected',
        selectedSuffix: ' selected',
      }
    : {
        step: 'Paso 2 · Elegí tu camino',
        title: '¿Cómo querés arrancar hoy?',
        subtitle: 'Elegí la forma que mejor acompaña tu energía en este momento.',
        personalTitle: 'Guía personal',
        personalDescription: 'Respondé el onboarding completo y recibí un plan a medida según tu ritmo.',
        personalDuration: '3-4 min',
        quickTitle: 'Inicio rápido',
        quickDescription: 'Activá en menos de un minuto con tareas curadas y setup compacto.',
        quickDuration: '< 1 min',
        quickLabel: 'Camino veloz',
        back: 'Volver',
        selected: 'Seleccionado',
        selectedSuffix: ' seleccionado',
      };

  const handlePathTap = (path: PathOption) => {
    if (isAdvancing) return;

    setSelectedPath(path);
    setIsAdvancing(true);

    window.setTimeout(() => {
      if (path === 'traditional') {
        onSelectTraditional();
        return;
      }

      onSelectQuickStart();
    }, ADVANCE_DELAY_MS);
  };

  return (
    <motion.section
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className="onboarding-premium-root onboarding-surface-base mx-auto w-full max-w-3xl rounded-3xl p-5 sm:p-7"
    >
      <p className="text-xs uppercase tracking-[0.2em] text-[color:var(--color-text-subtle)]">{copy.step}</p>
      <h2 className="mt-2 text-3xl font-semibold text-[color:var(--color-text)]">{copy.title}</h2>
      <p className="mt-2 text-sm text-[color:var(--color-text-muted)]">{copy.subtitle}</p>

      <div className="mt-6 grid gap-3.5 sm:grid-cols-2">
        {[
          {
            id: 'traditional' as const,
            emoji: '✨',
            title: copy.personalTitle,
            description: copy.personalDescription,
            chips: [
              {
                key: 'duration',
                className: 'border-[color:var(--color-border-soft)] bg-[color:var(--onboarding-premium-overlay-soft)] text-[color:var(--color-text-muted)]',
                text: copy.personalDuration,
              },
            ],
          },
          {
            id: 'quick_start' as const,
            emoji: '⚡',
            title: copy.quickTitle,
            description: copy.quickDescription,
            chips: [
              {
                key: 'duration',
                className: 'border-[color:var(--color-border-soft)] bg-[color:var(--onboarding-premium-overlay-soft)] text-[color:var(--color-text-muted)]',
                text: copy.quickDuration,
              },
              {
                key: 'label',
                className: 'border-[color:var(--color-border-soft)] bg-[color:var(--onboarding-premium-overlay-base)] text-[color:var(--color-text)]',
                text: copy.quickLabel,
              },
            ],
          },
        ].map((option) => {
          const isActive = selectedPath === option.id;
          const { palette, cardStyle, overlayStyle } = getPathCardStyle(isActive, option.id, theme);

          return (
            <motion.button
              key={option.id}
              type="button"
              whileTap={{ scale: 0.985 }}
              onClick={() => handlePathTap(option.id)}
              disabled={isAdvancing}
              aria-pressed={isActive}
              aria-label={`${option.title}${isActive ? copy.selectedSuffix : ''}`}
              data-selected={isActive ? 'true' : 'false'}
              className={[
                'glass-card onboarding-surface-inner onboarding-glass-border-soft relative flex h-full overflow-hidden rounded-3xl border p-4 text-left transition-all duration-250 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-accent-primary)] sm:p-5',
                isActive
                  ? 'ring-2 -translate-y-0.5 scale-[1.01] border-[color:var(--color-border-strong)] bg-[color:var(--onboarding-premium-overlay-base)] focus-visible:ring-4'
                  : 'border-[color:var(--color-border-soft)] hover:border-[color:var(--color-border-strong)] hover:-translate-y-0.5 hover:bg-[color:var(--onboarding-premium-overlay-soft)] focus-visible:border-[color:var(--color-border-strong)]',
                isAdvancing && !isActive ? 'opacity-75' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              style={cardStyle}
            >
              <span
                className={[
                  'pointer-events-none absolute inset-0 rounded-3xl transition-all duration-250 ease-out',
                  isActive ? 'opacity-100' : 'opacity-65',
                ].join(' ')}
                style={overlayStyle}
                aria-hidden
              />

              <span className="relative z-10 flex w-full flex-col gap-2.5">
                <span className="flex items-start justify-between gap-3">
                  <span className="flex items-center gap-2 text-lg font-semibold leading-tight text-[color:var(--color-text)]">
                    <span aria-hidden className="text-sm opacity-80">{option.emoji}</span>
                    {option.title}
                  </span>

                  {isActive ? (
                    <span
                      className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[0.58rem] font-semibold uppercase tracking-[0.08em] shadow-[0_0_14px_rgba(207,139,243,0.42)]"
                      style={{
                        background: palette.badgeBg,
                        color: palette.badgeText,
                        borderColor: palette.badgeBorder,
                      }}
                    >
                      <span aria-hidden>✓</span>
                      {copy.selected}
                    </span>
                  ) : null}
                </span>

                <p className="text-[0.95rem] leading-snug text-[color:var(--color-text-muted)]">{option.description}</p>

                <div className="mt-2 flex flex-wrap gap-2">
                  {option.chips.map((chip) => (
                    <span
                      key={chip.key}
                      className={`inline-flex rounded-full border px-2.5 py-1 text-[0.64rem] font-medium uppercase tracking-[0.12em] ${chip.className}`}
                    >
                      {chip.text}
                    </span>
                  ))}
                </div>
              </span>
            </motion.button>
          );
        })}
      </div>

      <div className="mt-8 flex justify-start">
        <button
          type="button"
          onClick={onBack}
          disabled={isAdvancing}
          className="inline-flex items-center gap-2 rounded-full border border-[color:var(--color-border-soft)] px-5 py-2 text-sm font-medium text-[color:var(--color-text-muted)] transition hover:border-[color:var(--color-border-strong)] hover:text-[color:var(--color-text)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-accent-primary)] disabled:cursor-not-allowed disabled:opacity-60"
        >
          ← {copy.back}
        </button>
      </div>
    </motion.section>
  );
}
